import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui/Button';

// Telemetry helper: fire-and-forget POST der chunk-error metadata. Server
// dedupliziert NICHT — jeder boundary-catch = 1 audit-row. Bei kurzem 4xx
// burst (z.B. rate-limit) silent-fail; nicht retry'en, wäre cascade-noise.
async function reportChunkError(error: Error): Promise<void> {
  await fetch('/api/audit/chunk-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: error.message.slice(0, 500),
      location: window.location.href.slice(0, 2048),
      userAgent: navigator.userAgent.slice(0, 500),
    }),
    // keepalive: Browser shipt die request auch bei page-unload (z.B. wenn
    // User direkt nach error-render reload klickt). Body-Limit 64KB — wir
    // schicken ~1KB, deutlich darunter.
    keepalive: true,
  });
}

// LazyChunkErrorBoundary — fängt React-render-errors aus dem children-tree,
// inklusive Suspense-thrown lazy-fetch-rejections (Network-Drop, post-deploy
// stale-asset-hash, CDN-flake, Browser-Cache-Korruption).
//
// Class-Component ist die EINZIGE React-API für Error-Boundaries (kein Hook
// in React 19). Erste Class-Component im Projekt — bleibt isoliert.
//
// Retry-Mechanismus: window.location.reload(). React.lazy() cached die
// failed-import-Promise file-level; key-bumping reicht NICHT zum Reset.
// Reload ist einfach + reliable für 3-5 User Setup. lazyWithRetry-Pattern
// als Followup-Sprint dokumentiert in memory/todo.md.

type Props = {
  children: ReactNode;
  fallback: ReactNode;
  /**
   * Wenn sich der `resetKey` ändert UND state.hasError ist, wird hasError auf
   * false zurückgesetzt → Boundary rendert wieder children. Genutzt für
   * route-change-reset (App.tsx übergibt aktuelles `location` aus wouter):
   * User navigiert nach Page-Chunk-Error via Sidebar woanders hin → Boundary
   * versucht erneut. Verhindert „stuck-on-ChunkErrorPanel"-Bug.
   *
   * NICHT als React-`key`-prop genutzt um remount zu vermeiden — das würde
   * den ganzen Switch+Suspense-Subtree bei jedem Route-Change unmounten +
   * Page-State (Scroll-Position, Selection) blow-away. componentDidUpdate
   * ist surgical: kein Effekt wenn kein Error aktiv ist.
   */
  resetKey?: string;
};

type State = {
  hasError: boolean;
};

export class LazyChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[lazy-chunk] load failed:', error, info.componentStack);
    // Fire-and-forget telemetry — Frontend wartet NICHT auf Response (Boundary
    // muss sofort die Fallback-UI rendern). Server-side rate-limit 3/min/IP
    // schützt gegen Spam; Backend-Errors werden silent-geschluckt da sie keine
    // User-Action auslösen sollen ("error reporting error" ist nicht hilfreich).
    void reportChunkError(error).catch(() => {
      // intentional: telemetry-fail darf nicht UI-feedback erzeugen
    });
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && this.props.resetKey !== prevProps.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// ChunkErrorPanel — visueller Fallback. fullscreen-Variant für unauthed-Branch
// (kein Layout-Shell drumherum), default für authed (INSIDE Layout).

type PanelProps = {
  fullscreen?: boolean;
};

export function ChunkErrorPanel({ fullscreen = false }: PanelProps): React.JSX.Element {
  const handleReload = (): void => {
    window.location.reload();
  };

  const wrapperClass = fullscreen
    ? 'min-h-screen flex items-center justify-center bg-brand-cream'
    : 'min-h-[300px] flex items-center justify-center px-4 py-10';

  return (
    <div className={wrapperClass} role="alert">
      <div className="max-w-md w-full bg-white border border-ink-200 rounded-lg shadow-sm p-6 text-center">
        <h2 className="text-lg font-semibold text-brand-green-700 mb-2">
          Inhalt konnte nicht geladen werden
        </h2>
        <p className="text-sm text-brand-green-600 mb-4">
          Eine Seite konnte nicht geladen werden — vermutlich ein Netzwerkproblem
          oder ein veraltetes Browser-Cache. Bitte lade die Seite neu.
        </p>
        <Button variant="primary" onClick={handleReload}>
          Seite neu laden
        </Button>
      </div>
    </div>
  );
}
